import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import UserCustomerUpdateManySelectorItem from "@/v2-backoffice-client/user/customer/update/many/UserCustomerUpdateManySelectorItem"

export type UserCustomerUpdateManySelectorProps = SelectorTriggerProps & {
  users: UserFragment[]
  onCompleted?(): void
}

/**
 * Selection-toolbar button that moves every selected user to one customer.
 * The customers that own some of the selection today are pinned first with
 * their counts, so a split selection is visible before the pick.
 */
function UserCustomerUpdateManySelector(
  props: UserCustomerUpdateManySelectorProps,
) {
  const { users, onCompleted, ...triggerProps } = props

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const ownerExternalIds = [
    ...new Set(
      users
        .map((user) => user.Customer?.ExternalCustomerId)
        .filter((id): id is string => Boolean(id)),
    ),
  ]

  const { data: ownerData, loading: ownersLoading } = useCustomerQuery({
    skip: !isOpen || ownerExternalIds.length === 0,
    variables: {
      input: {
        limit: ownerExternalIds.length,
        filters: [{ column: "ExternalCustomerId", in: ownerExternalIds }],
      },
    },
  })

  const { data, loading, error } = useCustomerQuery({
    skip: !isOpen || search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, { title: "Failed to query for customers" })

  const owners = ownerData?.getCustomers.items ?? []
  const ownerIds = new Set(owners.map((customer) => customer.Id))
  const results = (data?.getCustomers.items ?? []).filter(
    (customer) => !ownerIds.has(customer.Id),
  )

  return (
    <Selector
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setSelectedId(null)
      }}
    >
      <Selector.Trigger iconSrc="users-alt" {...triggerProps}>
        {triggerProps.children ?? "Customer"}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.Search
          placeholder="Search by customer name..."
          onSearch={setSearch}
        />

        <Selector.List
          emptyLabel="No customers found"
          isLoading={loading || ownersLoading}
          error={error}
          noResults={results.length === 0}
          pinned={owners.map((customer) => (
            <UserCustomerUpdateManySelectorItem
              key={customer.Id}
              customer={customer}
              users={users}
              added={customer.Id === selectedId}
              onSelect={() => {
                setSelectedId(customer.Id)
                onCompleted?.()
              }}
            />
          ))}
        >
          {results.map((customer) => (
            <UserCustomerUpdateManySelectorItem
              key={customer.Id}
              customer={customer}
              users={users}
              added={customer.Id === selectedId}
              onSelect={() => {
                setSelectedId(customer.Id)
                onCompleted?.()
              }}
            />
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default UserCustomerUpdateManySelector
